import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getLoginUrl, subscribeOnLoginSuccess } from '@sravni/utils/lib/auth';
import { useInterval } from '@sravni/react-utils';
import { provider } from '../../helpers/client';
import { ImportUserParams, SignInParams, VerifyParams } from '../../services/identity/identity.types';
import { authorizeUser, silentAuthorizeUser } from '../../store/reducers/user';
import { promiseTimeout, removeUndefined } from '../../helpers';
import { extendFormStore } from '../../store/reducers/formStore';
import { useAnalytics, EventType } from './analytics';

const SMS_RESEND_TIMEOUT = 60;

export const useSmsAuth = () => {
    const [countdown, setCountdown] = useState(0);
    const [attempts, setAttempts] = useState(0);
    const [inProcess, setInProcess] = useState(false);
    const [errorText, setErrorText] = useState('');
    const dispatch = useDispatch();
    const { pushEvent } = useAnalytics();

    useInterval(
        () => {
            setCountdown(Math.max(countdown - 1, 0));
        },
        countdown ? 1000 : null,
    );

    const requestCode = useCallback(
        async (params: SignInParams) => {
            if (countdown) {
                return;
            }

            setInProcess(true);
            setErrorText('');
            try {
                const { success, errorText } = await provider.signIn({
                    customer: 'Сайт',
                    productName: 'Подбор кредита',
                    ...params,
                });
                if (!success) {
                    setErrorText(errorText || 'Ошибка при запросе кода');
                }
            } catch (e) {
                console.error(e);
                setErrorText('Неизвестная ошибка');
                throw e;
            } finally {
                setInProcess(false);
            }

            setAttempts(attempts + 1);
            setCountdown(SMS_RESEND_TIMEOUT);
        },
        [attempts, countdown],
    );

    const checkCode = useCallback(
        async (params: VerifyParams & Partial<ImportUserParams>, withSilentAuth = false): Promise<string | null> => {
            setInProcess(true);
            setErrorText('');

            return new Promise<string>((resolve, reject) => {
                return Promise.race([provider.verify(params), promiseTimeout(1000 * 60, 'Истекло время ожидания')])
                    .then(async ({ otac, username, errorText }) => {
                        if (!otac || !username || errorText) {
                            return reject(new Error(errorText || 'Неверный СМС-код'));
                        }

                        const { phone, ...other } = params;

                        if (withSilentAuth && phone) {
                            const user = await provider.signInSilent(
                                removeUndefined({
                                    phone,
                                    ...other,
                                }) as any,
                            );
                            dispatch(
                                silentAuthorizeUser({
                                    sub: String(user.userId),
                                }),
                            );
                            dispatch(extendFormStore({ userId: user.userId }));
                            return resolve(String(user.userId));
                        }

                        const returnUrl = new URL(window.location.href);
                        returnUrl.searchParams.append('after_auth', 'true');

                        location.href = getLoginUrl({
                            loginType: 'otac',
                            otac,
                            hint: username,
                            returnUrl: returnUrl.href,
                            isNewWindow: false,
                            prompt: 'login',
                        });

                        const unsubscribe = subscribeOnLoginSuccess(async () => {
                            const account = await provider.account();
                            if (!account) {
                                return reject(new Error('Не удалось авторизоваться'));
                            }

                            setAttempts(0);
                            dispatch(authorizeUser(account));
                            setInProcess(false);
                            unsubscribe();
                            return resolve(account.sub);
                        });
                    })
                    .catch((e) => {
                        console.error(e);
                        return reject(new Error('Не удалось авторизоваться'));
                    });
            }).catch((e) => {
                console.error(e);
                setErrorText(e.message || 'Не удалось авторизоваться');
                setInProcess(false);

                pushEvent({
                    event: EventType.mainEvent,
                    eventAction: 'Неверный код СМС',
                });

                return null;
            });
        },
        [dispatch],
    );

    return { countdown, codeIsRequested: attempts > 0, requestCode, checkCode, inProcess, errorText, attempts };
};
